import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Testimonials.css';

// Shared testimonial data. `appId` matches the corresponding entry in
// LiveApps.js (LIVE_APPS) so quotes can be paired with the live preview card.
export const TESTIMONIALS = [
  {
    appId: 'coryblossom',
    quote:
      "ShawnyMedia built us a storefront that finally looks like our brand. The seasonal bouquet catalog and the subscription sign-up just work — customers order their monthly drops without ever emailing me to ask how. The website does the selling now, so I can stay focused on the flowers.",
    author: 'Cory',
    role: 'Owner, Cory Blossom',
    url: 'https://coryblossoms.netlify.app/',
    displayUrl: 'coryblossoms.netlify.app',
  },
  {
    appId: 'adams',
    quote:
      "The team delivered a clean, professional website that represents the office exactly the way I wanted. It loads fast, it works on every phone, and I can get updates out to residents in minutes. People genuinely comment on how polished the site looks.",
    author: 'Kevin Adams',
    role: 'Mayor, Village of Horseheads',
    url: 'https://kevindadams.com/',
    displayUrl: 'kevindadams.com',
  },
  {
    appId: 'luriecup',
    quote:
      "Our tournament used to live in scattered messages and flyers. ShawnyMedia put the whole story, registration, and donations on one website that supporters actually trust. Sign-ups climbed the moment the site went live.",
    author: 'Lurie Cup Organizing Committee',
    role: 'Charity Soccer Tournament',
    url: 'https://luriecup.org/',
    displayUrl: 'luriecup.org',
  },
  {
    appId: 'proscape',
    quote:
      "The site ShawnyMedia built makes us look like the established crew we are. The services pages and the quote form do the heavy lifting — homeowners find us, see exactly what we offer, and request an estimate without any phone tag. It's already brought in real jobs.",
    author: 'Isaac Moyers',
    role: 'Owner, ProScape NY',
    url: 'https://proscapeny.netlify.app/',
    displayUrl: 'proscapeny.netlify.app',
  },
];

// Quick lookup by appId for pairing quotes with Live Apps cards.
export const TESTIMONIAL_BY_APP = TESTIMONIALS.reduce((acc, t) => {
  acc[t.appId] = t;
  return acc;
}, {});

const Testimonials = () => {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const hoverRef = useRef(false);
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  // Pause whenever the pointer is hovering or actively dragging.
  const syncPaused = () => {
    pausedRef.current = hoverRef.current || dragRef.current.active;
  };

  // Keep scrollLeft within the first copy of the list so the loop is seamless.
  const wrapScroll = (el) => {
    const half = el.scrollWidth / 2;
    if (half <= 0) return;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft < 0) el.scrollLeft += half;
  };

  // Continuous auto-scroll. The track renders the list twice, so passing the
  // half-way point lets us jump back by half the width without a visible seam.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    // Respect users who prefer reduced motion: no auto-scroll (drag still works).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let raf;
    const speed = 0.5; // px per frame (~30px/s at 60fps)
    const step = () => {
      if (!pausedRef.current) {
        el.scrollLeft += speed;
        wrapScroll(el);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleMouseEnter = () => {
    hoverRef.current = true;
    syncPaused();
  };

  const handleMouseLeave = () => {
    hoverRef.current = false;
    syncPaused();
  };

  const handlePointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    dragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    el.classList.add('is-dragging');
    el.setPointerCapture?.(e.pointerId);
    syncPaused();
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current.active) return;
    const el = trackRef.current;
    if (!el) return;
    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 4) dragRef.current.moved = true;
    el.scrollLeft = dragRef.current.startScroll - dx;
    wrapScroll(el);
  };

  const handlePointerUp = (e) => {
    const el = trackRef.current;
    if (el) {
      el.classList.remove('is-dragging');
      el.releasePointerCapture?.(e.pointerId);
    }
    dragRef.current.active = false;
    syncPaused();
  };

  // Swallow the click that follows a drag so it doesn't open a testimonial link.
  const handleClickCapture = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = false;
    }
  };

  // Render the list twice for a seamless infinite loop.
  const loop = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section className="section testimonials-section">
      <div className="container">
        <div className="section-head">
          <p className="eyebrow">Client word</p>
          <h2 className="section-title reveal reveal-up">
            Real clients, real sites, real sentences.
          </h2>
          <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
            Every quote below belongs to a site that is live right now. Drag the
            row to read at your own pace.
          </p>
        </div>
      </div>

      <div
        className="testimonials-marquee reveal reveal-fade"
        ref={trackRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleClickCapture}
      >
        <div className="testimonials-track">
          {loop.map((t, idx) => (
            <figure
              className="testimonial-card testimonial-card--marquee"
              key={`${t.appId}-${idx}`}
              aria-hidden={idx >= TESTIMONIALS.length}
            >
              <div className="testimonial-quote-mark" aria-hidden="true">&ldquo;</div>
              <blockquote className="testimonial-quote">{t.quote}</blockquote>
              <figcaption className="testimonial-author">
                <span className="testimonial-author-name">{t.author}</span>
                <span className="testimonial-author-role">{t.role}</span>
                <a
                  className="testimonial-author-site"
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  draggable="false"
                >
                  {t.displayUrl}
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <div className="container">
        <div className="testimonials-cta reveal reveal-up">
          <Link to="/live-apps" className="btn btn--ghost">
            See their live sites <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
